import {
  DRAFT_VALIDATION_PAGE_SIZE,
  type DraftValidationListItem,
} from "../../api/queries/adminQueries";
import { ReceivablesPagination } from "../receivables/ReceivablesPagination";

type DraftListTableProps = {
  drafts: DraftValidationListItem[];
  totalDrafts: number;
  page: number;
  totalPages: number;
  isLoading: boolean;
  isFetching?: boolean;
  onPageChange: (page: number) => void;
  onSelect: (uid: string) => void;
  formatDate: (iso: string) => string;
};

const shortHash = (
  key: string | null | undefined,
  hash: string | null | undefined,
) => {
  if (!key) return "—";
  const h = hash ? hash.slice(0, 6) : "";
  return h ? `${key} (${h})` : key;
};

export const DraftListTable = ({
  drafts,
  totalDrafts,
  page,
  totalPages,
  isLoading,
  isFetching = false,
  onPageChange,
  onSelect,
  formatDate,
}: DraftListTableProps) => {
  const pageStart =
    totalDrafts === 0 ? 0 : (page - 1) * DRAFT_VALIDATION_PAGE_SIZE + 1;
  const pageEnd =
    totalDrafts === 0
      ? 0
      : Math.min(pageStart + drafts.length - 1, totalDrafts);

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="p-5">
        <h2 className="mb-4 text-lg font-semibold text-slate-900">
          Drafts ({totalDrafts.toLocaleString()})
        </h2>
        {isLoading ? (
          <p className="text-sm text-slate-600">Loading drafts…</p>
        ) : totalDrafts === 0 ? (
          <p className="text-sm text-slate-600">
            No drafts found for this organization.
          </p>
        ) : (
          <div className="max-h-[600px] overflow-auto">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="sticky top-0 bg-slate-50">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-slate-700">
                    Customer
                  </th>
                  <th className="px-3 py-2 text-left font-medium text-slate-700">
                    Feedback
                  </th>
                  <th className="px-3 py-2 text-left font-medium text-slate-700">
                    Trace
                  </th>
                  <th className="px-3 py-2 text-left font-medium text-slate-700">
                    Model
                  </th>
                  <th className="px-3 py-2 text-left font-medium text-slate-700">
                    Prompt
                  </th>
                  <th className="px-3 py-2 text-left font-medium text-slate-700">
                    Edit
                  </th>
                  <th className="px-3 py-2 text-left font-medium text-slate-700">
                    Subject
                  </th>
                  <th className="px-3 py-2 text-left font-medium text-slate-700">
                    Created
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {drafts.map((draft) => (
                  <tr
                    key={draft.draft_uid}
                    onClick={() => onSelect(draft.draft_uid)}
                    className="cursor-pointer transition-colors hover:bg-slate-50"
                  >
                    <td className="px-3 py-2 text-slate-900">
                      {draft.customer_name}
                    </td>
                    <td className="px-3 py-2 text-slate-600">
                      {draft.feedback_sentiment === "like"
                        ? "👍"
                        : draft.feedback_sentiment === "dislike"
                          ? "👎"
                          : "—"}
                    </td>
                    <td className="px-3 py-2 text-slate-600">
                      {draft.has_trace ? (
                        <span className="inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-800">
                          {draft.trace_status ?? "traced"}
                        </span>
                      ) : (
                        <span className="text-xs text-slate-400">No trace</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-slate-600">
                      {draft.model_name ?? "—"}
                    </td>
                    <td
                      className="max-w-[140px] truncate px-3 py-2 text-slate-600"
                      title={draft.prompt_template_key ?? undefined}
                    >
                      {shortHash(draft.prompt_template_key, null)}
                    </td>
                    <td className="px-3 py-2 text-slate-600">
                      {draft.composer_edit_distance != null
                        ? draft.composer_edit_distance
                        : "—"}
                    </td>
                    <td className="px-3 py-2 text-slate-600">
                      {draft.subject || "–"}
                    </td>
                    <td className="px-3 py-2 text-slate-600">
                      {formatDate(draft.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {totalDrafts > 0 ? (
        <ReceivablesPagination
          page={page}
          totalPages={totalPages}
          onPageChange={onPageChange}
          isFetching={isFetching}
          summary={
            <>
              Showing {pageStart.toLocaleString()}–{pageEnd.toLocaleString()} of{" "}
              {totalDrafts.toLocaleString()} drafts
            </>
          }
        />
      ) : null}
    </div>
  );
};
